"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StoredPcbLayoutNodeSchema = exports.StoredBasePcbLayoutNodeSchema = void 0;
const zod_1 = require("zod");
const rule_1 = require("../rule");
const structure_1 = require("./structure");
/*
 * Schemas for storing pcb objects in Firestore.  For the most part, pcb layout nodes and rules
 * are stored in the database precisely as they are in memory - with one big exception.
 *
 * We _explicitly_ do not store baked or flattened rules in the database, and the schemas are defined
 * as such.  This is because the baked and flattened rules are in-memory values only - they should not
 * be passed out of the client and used in another context.
 */
/**
 * All pcb layout nodes share some common structure.
 */
exports.StoredBasePcbLayoutNodeSchema = zod_1.z.object({
    uid: zod_1.z.string(),
    name: zod_1.z.string(),
    type: zod_1.z.nativeEnum(structure_1.PcbNodeTypes),
    parentUid: zod_1.z.string(),
    // TODO: ideally the key would be an enum, but deserializing is an issue bc zod doesn't do exhaustiveness checks on the keys, and the
    // partial typing is an issue.  So here, instead of defining the record type explicitly, we allow zod to deserialize anything but
    // validate that the key is in fact a layout rule key.
    pcbNodeRuleSet: zod_1.z.record(zod_1.z.string().refine((item) => item in rule_1.ruleKeys), rule_1.StoredPcbLayoutRuleSchema, rule_1.StoredPcbLayoutRuleSetSchema),
    userCodeData: zod_1.z.optional(zod_1.z.any()),
    childUids: zod_1.z.array(zod_1.z.string()),
});
/*
 * Individual PCB layout nodes all have different types and are disambiguated as a discriminated union.  It is possible
 * to just declare the schema as having a single key for the type, but instead we can define the individual types as extensions
 * of the base schema to help the zod parser and significantly speed up its work.
 */
// Element nodes
const StoredPcbLayoutElementNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.element),
});
// Via nodes
const StoredPcbLayoutViaNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.via),
});
// Net nodes
const StoredPcbLayoutNetNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.net),
});
// Route segment nodes.  Note that the associated type is exported because it is structurally different from the base type.
const StoredPcbLayoutRouteSegmentNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.routeSegment),
    startId: zod_1.z.string(),
    endId: zod_1.z.string(),
});
// Text nodes
const StoredPcbLayoutTextNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.text),
});
// Root nodes
const StoredPcbLayoutRootNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.root),
});
// Pad nodes.  Note that the associated type is exported because it is structurally different from the base type.
const StoredPcbLayoutPadNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.pad),
    terminalId: zod_1.z.optional(zod_1.z.string()),
});
// Footprint nodes
const StoredPcbLayoutFootprintNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.footprint),
});
// Group nodes
const StoredPcbLayoutGroupNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.group),
});
// Layout nodes
const StoredPcbLayoutLayoutNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.layout),
});
// Container nodes
const StoredPcbLayoutContainerNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.container),
});
// Model nodes
const StoredPcbLayoutModelNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.model),
});
// Line nodes
const StoredPcbLayoutLineNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.line),
});
// Rectangle nodes
const StoredPcbLayoutRectangleNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.rectangle),
});
// Circle nodes
const StoredPcbLayoutCircleNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.circle),
});
// Fill nodes
const StoredPcbLayoutFillNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.fill),
});
// Zone nodes
const StoredPcbLayoutZoneNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.zone),
});
// Smart via nodes
const StoredPcbLayoutSmartViaNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.smartVia),
});
// Polygon nodes
const StoredPcbLayoutPolygonNodeSchema = zod_1.z.object({
    ...exports.StoredBasePcbLayoutNodeSchema.shape,
    type: zod_1.z.literal(structure_1.PcbNodeTypes.polygon),
});
/**
 * A discriminated union schema for any type of pcb layout node.
 *
 * Declare the type upfront here, so that zod doesn't need to infer it
 * when using this schema. This helps avoid TS2742:
 *
 * "The inferred type of this node exceeds the maximum length the compiler will serialize. An explicit type annotation is needed."
 */
exports.StoredPcbLayoutNodeSchema = zod_1.z.discriminatedUnion("type", [
    StoredPcbLayoutRectangleNodeSchema,
    StoredPcbLayoutCircleNodeSchema,
    StoredPcbLayoutLineNodeSchema,
    StoredPcbLayoutContainerNodeSchema,
    StoredPcbLayoutElementNodeSchema,
    StoredPcbLayoutFillNodeSchema,
    StoredPcbLayoutFootprintNodeSchema,
    StoredPcbLayoutGroupNodeSchema,
    StoredPcbLayoutLayoutNodeSchema,
    StoredPcbLayoutModelNodeSchema,
    StoredPcbLayoutNetNodeSchema,
    StoredPcbLayoutRouteSegmentNodeSchema,
    StoredPcbLayoutRootNodeSchema,
    StoredPcbLayoutPadNodeSchema,
    StoredPcbLayoutViaNodeSchema,
    StoredPcbLayoutTextNodeSchema,
    StoredPcbLayoutZoneNodeSchema,
    StoredPcbLayoutSmartViaNodeSchema,
]);
//# sourceMappingURL=schema.js.map